import React, { useCallback, useEffect, useState } from 'react';
import { Star, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { FadeIn } from './FadeIn';
import { AnimatePresence, motion } from 'framer-motion';
const testimonials = [{
  text: '"El mejor tatuaje que me he hecho en mi vida. El nivel de detalle en el realismo es increíble. Llevé una foto y lo que me hizo superó todas mis expectativas."',
  author: 'Maria G.',
  info: 'Realismo Retrato · Miami, FL',
  initial: 'M'
}, {
  text: '"Venía muy nerviosa porque era mi primer tatuaje grande. El artista me explicó todo el proceso, me hizo sentir completamente segura, y el resultado es simplemente espectacular."',
  author: 'Jessica R.',
  info: 'Fine Line · Miami, FL',
  initial: 'J'
}, {
  text: '"15 años de experiencia se notan desde el primer trazo. Tiene una precisión y un dominio del color que no he visto en ningún otro estudio de Miami."',
  author: 'Carlos M.',
  info: 'Manga Completa · Miami, FL',
  initial: 'C'
}, {
  text: '"Quería un retrato de mi abuela y estaba muy preocupado de que no quedara bien. El resultado me dejó sin palabras. Ella hubiera estado orgullosa."',
  author: 'Andres L.',
  info: 'Retrato Realista · Miami, FL',
  initial: 'A'
}, {
  text: '"Fui con una idea muy vaga y él la transformó en una obra de arte. Su formación artística se nota: sabe de composición, de luz, de color. No es solo tatuador, es artista."',
  author: 'Sofia P.',
  info: 'Diseño Personalizado · Miami, FL',
  initial: 'S'
}, {
  text: '"Llevo 8 tatuajes y este es definitivamente el mejor. Ambiente profesional, higiene impecable, y un artista que realmente escucha lo que quieres."',
  author: 'Roberto K.',
  info: 'Blackwork · Miami, FL',
  initial: 'R'
}];
export function Testimonials() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const goTo = useCallback((index: number) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  }, [current]);
  const next = useCallback(() => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % testimonials.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);
  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);
  const t = testimonials[current];
  return <section id="testimonials" className="py-[50px] px-[25px] md:py-[60px] md:px-[40px] lg:py-[70px] lg:px-[60px] bg-dark" data-id="element-256">
      <FadeIn className="text-center mb-[40px]" data-id="element-257">
        <span className="inline-block text-[0.6rem] tracking-[6px] uppercase text-gold mb-[15px] font-semibold" data-id="element-258">
          Lo que dicen
        </span>
        <h2 className="font-cinzel text-[clamp(1.5rem,3.5vw,2.5rem)] text-white uppercase tracking-[4px] mb-4" data-id="element-259">
          Opiniones de <span className="text-gold" data-id="element-260">Clientes</span>
        </h2>
        <div className="w-[60px] h-[2px] bg-gold mx-auto" data-id="element-261"></div>
      </FadeIn>

      <div className="max-w-[650px] mx-auto relative" data-id="element-262">
        <div className="min-h-[220px] flex items-center" data-id="element-263">
          <AnimatePresence mode="wait" custom={direction} data-id="element-264">
            <motion.div key={current} custom={direction} initial={{
            opacity: 0,
            x: direction * 60
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: direction * -60
          }} transition={{
            duration: 0.4,
            ease: 'easeInOut'
          }} className="w-full bg-dark-2 border border-gold/10 p-[30px_25px] md:p-[35px_35px] relative text-center" data-id="element-265">
              <div className="absolute top-[10px] right-[15px] font-cinzel text-[4rem] text-gold/[0.08] leading-none" data-id="element-266">
                &ldquo;
              </div>

              <div className="flex gap-1 mb-4 justify-center" data-id="element-267">
                {[...Array(5)].map((_, j) => <Star key={j} size={12} className="text-gold fill-gold" data-id="element-268" />)}
              </div>

              <p className="text-gray text-[0.85rem] leading-[1.8] mb-5 italic tracking-[0.3px] relative z-10" data-id="element-269">
                {t.text}
              </p>

              <div className="flex items-center gap-3 justify-center border-t border-gold/10 pt-4" data-id="element-270">
                <div className="w-[38px] h-[38px] rounded-full bg-dark-3 border-2 border-gold/30 flex items-center justify-center font-cinzel text-[0.9rem] text-gold font-bold shrink-0" data-id="element-271">
                  {t.initial}
                </div>
                <div className="text-left" data-id="element-272">
                  <div className="font-cinzel text-[0.8rem] text-white tracking-[2px] uppercase" data-id="element-273">
                    {t.author}
                  </div>
                  <div className="text-[0.65rem] text-gold tracking-[1px] mt-[2px]" data-id="element-274">
                    {t.info}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <button onClick={prev} className="absolute left-[-15px] md:left-[-45px] top-1/2 -translate-y-1/2 w-[35px] h-[35px] border border-gold/20 flex items-center justify-center text-gold bg-dark-2 transition-all duration-300 hover:border-gold hover:bg-gold/10" aria-label="Opinión anterior" data-id="element-275">
          <ChevronLeftIcon size={18} data-id="element-276" />
        </button>
        <button onClick={next} className="absolute right-[-15px] md:right-[-45px] top-1/2 -translate-y-1/2 w-[35px] h-[35px] border border-gold/20 flex items-center justify-center text-gold bg-dark-2 transition-all duration-300 hover:border-gold hover:bg-gold/10" aria-label="Siguiente opinión" data-id="element-277">
          <ChevronRightIcon size={18} data-id="element-278" />
        </button>

        <div className="flex justify-center gap-2 mt-6" data-id="element-279">
          {testimonials.map((_, i) => <button key={i} onClick={() => goTo(i)} className={`w-2 h-2 rounded-full transition-all duration-300 ${i === current ? 'bg-gold w-5' : 'bg-gold/25 hover:bg-gold/50'}`} aria-label={`Ir a opinión ${i + 1}`} data-id="element-280" />)}
        </div>
      </div>
    </section>;
}